import { baseApi } from '../api/baseApi'
import { PLATFORM_PHOTO_SETTINGS_URL } from '../api/urls'

/** Тег кэша настроек раздела «Фото» (RP-CACHE-0): после сохранения настройки перечитываются. */
export const PHOTO_SETTINGS_TAG = 'PhotoSettings'

/** Допустимые пределы настроек — их присылает сервер, экран только подписывает поля; решает сервер. */
export interface PhotoSettingsRanges {
  minStagingLifetimeDays: number
  maxStagingLifetimeDays: number
  minFramesTtlDays: number
  maxFramesTtlDays: number
  minFramesMaxBytes: number
  maxFramesMaxBytes: number
}

/**
 * Настройки раздела «Фото»: сроки в сутках, потолок кадров поиска в байтах. `version` 0 — ещё не сохранялись (значения по умолчанию);
 * правка идёт поверх версии, которую человек видел.
 */
export interface PhotoSettings {
  stagingLifetimeDays: number
  framesTtlDays: number
  framesMaxBytes: number
  version: number
  updatedAt: string | null
  updatedBy: string | null
  ranges: PhotoSettingsRanges
}

/** Что человек просит записать. */
export interface PhotoSettingsUpdate {
  stagingLifetimeDays: number
  framesTtlDays: number
  framesMaxBytes: number
  version: number
}

function record(raw: unknown, what: string): Record<string, unknown> {
  if (typeof raw !== 'object' || raw === null) throw new Error(`настройки «Фото»: ожидался объект (${what})`)
  return raw as Record<string, unknown>
}

function whole(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value)) throw new Error(`настройки «Фото»: ожидалось целое число (${what})`)
  return value
}

const textOrNull = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null)

/** Ответ без числа или пределов не показывается как «нули»: экран говорит, что ответ непонятен, а не подставляет значения. */
export function decodePhotoSettings(raw: unknown): PhotoSettings {
  const value = record(raw, 'ответ')
  const ranges = record(value.ranges, 'ranges')
  return {
    stagingLifetimeDays: whole(value.stagingLifetimeDays, 'stagingLifetimeDays'),
    framesTtlDays: whole(value.framesTtlDays, 'framesTtlDays'),
    framesMaxBytes: whole(value.framesMaxBytes, 'framesMaxBytes'),
    version: whole(value.version, 'version'),
    updatedAt: textOrNull(value.updatedAt),
    updatedBy: textOrNull(value.updatedBy),
    ranges: {
      minStagingLifetimeDays: whole(ranges.minStagingLifetimeDays, 'minStagingLifetimeDays'),
      maxStagingLifetimeDays: whole(ranges.maxStagingLifetimeDays, 'maxStagingLifetimeDays'),
      minFramesTtlDays: whole(ranges.minFramesTtlDays, 'minFramesTtlDays'),
      maxFramesTtlDays: whole(ranges.maxFramesTtlDays, 'maxFramesTtlDays'),
      minFramesMaxBytes: whole(ranges.minFramesMaxBytes, 'minFramesMaxBytes'),
      maxFramesMaxBytes: whole(ranges.maxFramesMaxBytes, 'maxFramesMaxBytes'),
    },
  }
}

export const photoSettingsApi = baseApi.enhanceEndpoints({ addTagTypes: [PHOTO_SETTINGS_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    /** Настройки «Фото» (право `platform:photo:settings`). GET можно безопасно повторить при сбое сети. */
    photoSettings: build.query<PhotoSettings, void>({
      query: () => ({ url: PLATFORM_PHOTO_SETTINGS_URL, decode: decodePhotoSettings, readOnly: true }),
      providesTags: [{ type: PHOTO_SETTINGS_TAG, id: 'SETTINGS' }],
    }),
    /** Сохранить настройки поверх увиденной версии: обгон другим человеком — 409 со словами сервера. */
    photoSettingsSave: build.mutation<PhotoSettings, PhotoSettingsUpdate>({
      query: (body) => ({ url: PLATFORM_PHOTO_SETTINGS_URL, method: 'PUT', body, decode: decodePhotoSettings }),
      invalidatesTags: [{ type: PHOTO_SETTINGS_TAG, id: 'SETTINGS' }],
    }),
  }),
})

export const { usePhotoSettingsQuery, usePhotoSettingsSaveMutation } = photoSettingsApi
